import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar } from 'react-bootstrap';
import { auth, db } from '../../firebase.ts';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { getInProgressTest } from '../../services/pronunciationTestService';
import { isTabletPortrait, setupDeviceDetection } from '../../utils/deviceDetection';
import { getAllUserProgress, getCompletedModules } from '../../services/moduleProgressService';
import MemoryGame from '../Games/Memory/MemoryGame';
import StoryGame from '../Games/Story/StoryGame';
import WordMatchGame from '../Games/WordMatch/WordMatchGame';
import GamesOverview from '../Games/GamesOverview';
import LearningMaterials from '../LearningMaterials/LearningMaterials';
import ModuleOverview from '../Modules/ModuleOverview';
import ModuleView from '../Modules/ModuleView';
import SuchbilderGame from '../Games/Suchbilder/SuchbilderGame';
import MinimalPairGame from '../Games/MinimalPair/MinimalPairGame';
import Settings from '../Settings/Settings';
import WelcomeModal from '../Dashboard/WelcomeModal';
import AusspracheTestModal from '../Dashboard/AusspracheTestModal';
import DifficultyModal from '../Modals/DifficultyModal';
import SubscriptionPage from '../../pages/SubscriptionPage';
import TheoreticalBackground from '../Theory/TheoreticalBackground';
import Icon from '../UI/Icon';
import { RiCheckLine } from "react-icons/ri";
import sprechheldenHeld from '../../assets/sprechheldenHeld.png';

function Dashboard({ activeGame, selectedSyllables, onGameSelect, difficulty, setActiveGame, userData, onSettingsSaved }) {
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedModuleData, setSelectedModuleData] = useState(null);
  const [moduleCompleteFunction, setModuleCompleteFunction] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showAusspracheTest, setShowAusspracheTest] = useState(false);
  const [inProgressTestData, setInProgressTestData] = useState(null); // Store in-progress test from new collection
  const [showDifficultyModal, setShowDifficultyModal] = useState(false);
  const [selectedGameInfo, setSelectedGameInfo] = useState({ id: null, name: '' });
  const [localDifficulty, setLocalDifficulty] = useState('medium');
  const [selectedMaterialCategory, setSelectedMaterialCategory] = useState(null);
  const [isTabletPortraitMode, setIsTabletPortraitMode] = useState(false);
  const [currentModuleData, setCurrentModuleData] = useState(null);
  const [watchedVideos, setWatchedVideos] = useState([]);

  // Check if user has monthly_promo plan (games-only access)
  const hasGamesOnlyAccess = () => {
    if (!userData || !userData.subscription) return false;
    return userData.subscription.planKey === 'monthly_promo' ||
           userData.subscription.type === 'monthly_promo';
  };
  
  const handleBackToModules = () => {
    setSelectedModuleId(null);
    setSelectedModuleData(null);
    setModuleCompleteFunction(null);
  };

  const handleNavigateToNextModule = (completedModuleOrder) => {
    // This function will be implemented to find and navigate to the next module
    // For now, we'll go back to the module overview where the next module will be unlocked
    
    // Go back to module overview where the next module should now be unlocked
    setSelectedModuleId(null);
    setSelectedModuleData(null);
    setModuleCompleteFunction(null);
  };

  const handleModuleSelect = (moduleId, moduleData, completeFunction) => {
    setSelectedModuleId(moduleId);
    setSelectedModuleData(moduleData);
    setModuleCompleteFunction(() => completeFunction);
  };

  const getGameName = (gameId) => {
    const names = {
      memory: 'Memory Spiel',
      story: 'Geschichte erzählen',
      wordmatch: 'Wörter verbinden',
      suchbilder: 'Sprechhelden auf Lautsuche',
      minimalpair: 'Laut-Detektive'
    };
    return names[gameId] || '';
  };

  const handleNavigateToGame = (gameId) => {
    // Map game IDs to activeGame values
    const gameMap = {
      'memory': 'memory',
      'story': 'story',
      'wordmatch': 'wordmatch',
      'connect': 'wordmatch', // Connect game maps to WordMatchGame
      'suchbilder': 'suchbilder',
      'search': 'suchbilder', // Search game maps to SuchbilderGame
      'minimalpair': 'minimalpair',
      'minimal': 'minimalpair' // Minimal game maps to MinimalPairGame
    };
    
    if (gameMap[gameId]) {
      // Set the selected game info and show difficulty modal
      setSelectedGameInfo({ id: gameMap[gameId], name: getGameName(gameMap[gameId]) });
      setShowDifficultyModal(true);
    } else {
      alert(`Spiel "${gameId}" ist noch nicht verfügbar.`);
    }
  };

  const handleDifficultySelect = (selectedDifficulty) => {
    setLocalDifficulty(selectedDifficulty);
    setActiveGame(selectedGameInfo.id);
    setShowDifficultyModal(false);
  };

  const handleNavigateToMaterials = (categoryId) => {
    setSelectedMaterialCategory(categoryId);
    setActiveGame('materials');
  };


  // Clear material category when navigating away from materials
  useEffect(() => {
    if (activeGame !== 'materials' && selectedMaterialCategory !== null) {
      setSelectedMaterialCategory(null);
    }
  }, [activeGame, selectedMaterialCategory]);

  // Tablet portrait detection
  useEffect(() => {
    const cleanup = setupDeviceDetection(() => {
      setIsTabletPortraitMode(isTabletPortrait());
    });
    return cleanup;
  }, []);

  // Load watched videos from localStorage
  useEffect(() => {
    const videos = JSON.parse(localStorage.getItem('watchedVideos') || '[]');
    setWatchedVideos(videos);
  }, [activeGame]); // Reload when returning to home

  // Helper function to calculate current module data
  const calculateCurrentModuleData = React.useCallback((allModules, progressData, completedModulesList) => {
    if (!allModules || allModules.length === 0) return null;

    // Find the first module that is not completed or has progress
    let currentModule = null;
    let currentLesson = null;
    let currentStep = null;

    // Find first incomplete module
    for (const module of allModules) {
      const isCompleted = completedModulesList.includes(module.order);

      // Skip completed modules unless it's the last one
      if (!isCompleted || module === allModules[allModules.length - 1]) {
        currentModule = module;

        // Find current lesson within this module
        if (module.subsections && module.subsections.length > 0) {
          const moduleProgress = progressData[module.id] || { steps: [], sections: [] };

          // Find first incomplete lesson
          for (let i = 0; i < module.subsections.length; i++) {
            const subsection = module.subsections[i];
            const allItemsCompleted = subsection.items?.every(item => {
              const stepId = `${subsection.id || `${module.id}-${i + 1}`}-${subsection.items.indexOf(item) + 1}`;
              return moduleProgress.steps.includes(stepId);
            }) || false;

            if (!allItemsCompleted || i === module.subsections.length - 1) {
              currentLesson = {
                id: i + 1,
                title: subsection.title,
                completed: allItemsCompleted
              };

              // Find current step within this lesson
              if (subsection.items && subsection.items.length > 0) {
                for (let j = 0; j < subsection.items.length; j++) {
                  const item = subsection.items[j];
                  const stepId = `${subsection.id || `${module.id}-${i + 1}`}-${j + 1}`;
                  const isStepCompleted = moduleProgress.steps.includes(stepId);

                  if (!isStepCompleted || j === subsection.items.length - 1) {
                    currentStep = {
                      title: getItemTitle(item),
                      stepNumber: j + 1,
                      totalSteps: subsection.items.length,
                      type: item.type || 'material'
                    };
                    break;
                  }
                }
              }
              break;
            }
          }
        }

        if (!isCompleted) break;
      }
    }

    if (!currentModule) return null;

    // Build lessons array
    const lessons = currentModule.subsections?.map((subsection, i) => {
      const moduleProgress = progressData[currentModule.id] || { steps: [], sections: [] };
      const allItemsCompleted = subsection.items?.every(item => {
        const stepId = `${subsection.id || `${currentModule.id}-${i + 1}`}-${subsection.items.indexOf(item) + 1}`;
        return moduleProgress.steps.includes(stepId);
      }) || false;

      return {
        id: i + 1,
        title: subsection.title,
        completed: allItemsCompleted,
        current: currentLesson && currentLesson.id === i + 1
      };
    }) || [];

    return {
      module: {
        id: currentModule.id,
        title: currentModule.title,
        currentLesson: currentLesson ? currentLesson.id : 1,
        totalLessons: currentModule.subsections?.length || 0
      },
      lessons,
      currentStep: currentStep || {
        title: 'Modul starten',
        stepNumber: 1,
        totalSteps: 1,
        type: 'material'
      }
    };
  }, []);

  // Helper function to get item title based on type
  const getItemTitle = (item) => {
    if (item.title) return item.title;

    const typeNames = {
      'material': 'Lernmaterial',
      'game': 'Spiel',
      'video': 'Video',
      'worksheet': 'Arbeitsblatt',
      'task': 'Aufgabe',
      'explanation': 'Erklärung',
      'category': 'Materialien'
    };

    return typeNames[item.type] || 'Schritt';
  };

  // Fetch modules and user progress
  useEffect(() => {
    const fetchModulesAndProgress = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        // Fetch all modules
        const modulesSnapshot = await getDocs(collection(db, 'learning_modules'));
        const fetchedModules = [];
        modulesSnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedModules.push({
            id: data.order || fetchedModules.length + 1,
            order: data.order,
            title: data.title,
            description: data.description,
            subsections: data.subsections || [],
            checklist: data.checklist || [],
            firebaseId: doc.id
          });
        });

        // Sort by order
        fetchedModules.sort((a, b) => (a.order || 0) - (b.order || 0));

        // Fetch user progress
        const progress = await getAllUserProgress(user.uid);

        // Fetch completed modules
        const completed = await getCompletedModules(user.uid);

        // Calculate current module data
        const currentData = calculateCurrentModuleData(fetchedModules, progress.modules || {}, completed);
        setCurrentModuleData(currentData);
      } catch (error) {
        console.error('Error fetching modules and progress:', error);
      }
    };

    if (activeGame === 'home') {
      fetchModulesAndProgress();
    }
  }, [activeGame, userData, calculateCurrentModuleData]);

  // Show welcome modal when user first visits the home screen
  useEffect(() => {
    const checkForInProgressTest = async () => {
      if (activeGame === 'home' && userData) {
        // Check for in-progress test from new collection
        const user = auth.currentUser;
        if (user) {
          const inProgressTest = await getInProgressTest(user.uid);

          if (inProgressTest) {
            setInProgressTestData(inProgressTest);
            setShowAusspracheTest(true);
            return;
          }
        }

        // LEGACY: Check old location for backward compatibility
        if (userData.ausspracheTestState?.isActive) {
          // Convert old format to new format for the modal
          setInProgressTestData({
            id: null, // No ID for legacy tests
            data: userData.ausspracheTestState
          });
          setShowAusspracheTest(true);
          return;
        }

        // Check if modal has been shown before from user data
        const hasSeenWelcomeModal = userData.hasSeenWelcomeModal;

        if (!hasSeenWelcomeModal) {
          // Add a small delay to ensure smooth rendering
          const timer = setTimeout(() => {
            setShowWelcomeModal(true);
          }, 500);

          return () => clearTimeout(timer);
        }
      }
    };

    checkForInProgressTest();
  }, [activeGame, userData]);

  const renderHomeScreen = () => {
    // Use dynamic module data or fallback to defaults
    const currentModule = currentModuleData?.module || { id: 1, title: 'Wahrnehmung', currentLesson: 1, totalLessons: 1 };
    const lessons = currentModuleData?.lessons || [
      { id: 1, title: 'Modul starten', completed: false, current: true }
    ];
    const currentStep = currentModuleData?.currentStep || { title: 'Modul starten', stepNumber: 1, totalSteps: 1, type: 'material' };

    return (
      <Container fluid className="px-3 px-md-4">
        <div className="mb-4 mb-md-5">
          <h1 className="mb-2 mb-md-3 welcome-title text-center text-md-start">
            Hallo, {userData?.role === 'professional'
              ? userData?.displayName
              : `Familie ${userData?.displayName?.split(' ').pop() || 'Benutzer'}`
            }! <Icon name="wave" size="lg" className="wave-icon" />
          </h1>
          <p className="text-secondary text-center text-md-start">
            Willkommen zurück! Hier ist dein aktueller Lernfortschritt.
          </p>
        </div>

        <Row className="g-3 g-md-4">
          <Col xs={12} lg={8} className="order-2 order-lg-1">
            <div className="progress-box-vertical">
              <div className="progress-header mb-3 mb-md-4">
                <div className="progress-icon">
                  <Icon name="modules" size="lg" />
                </div>
                <div className="flex-grow-1">
                  <h4 className="mb-1">Lernmodul {currentModule.id}: {currentModule.title}</h4>
                  <p className="text-muted mb-0">
                    Lektion {currentModule.currentLesson} von {currentModule.totalLessons}
                  </p>
                </div>
              </div>

              {/* Lesson Timeline - Hidden in tablet portrait mode */}
              {!isTabletPortraitMode && (
                <div className="lesson-timeline d-none d-md-block">
                  {lessons.map((lesson, index) => (
                    <div key={lesson.id} className={`timeline-item ${lesson.completed ? 'completed' : ''} ${lesson.current ? 'current' : ''}`}>
                      <div className="timeline-marker">
                        {lesson.completed ? <Icon name="check" size="sm" /> : index + 1}
                      </div>
                      {lesson.current && (
                        <div className="hero-character d-none d-md-block">
                          <img src={sprechheldenHeld} alt="Sprechheld" className="hero-image" />
                        </div>
                      )}
                      <div className="timeline-content">
                        <h5 className={`mb-1 ${lesson.current ? 'text-primary' : ''}`}>
                          {lesson.title}
                        </h5>
                        {lesson.current && (
                          <div className="current-step-info">
                            <p className="mb-2 text-muted">
                              Aktueller Schritt: <strong>{currentStep.title}</strong>
                            </p>
                            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                              <span className="step-type-badge">
                                {currentStep.type === 'video' ? <><Icon name="video" size="sm" /> Video</> :
                                 currentStep.type === 'task' ? <><Icon name="task" size="sm" /> Aufgabe</> :
                                 currentStep.type === 'worksheet' ? <><Icon name="worksheet" size="sm" /> Arbeitsblatt</> :
                                 <><Icon name="explanation" size="sm" /> Erklärung</>}
                              </span>
                              <small className="text-muted">
                                Schritt {currentStep.stepNumber} von {currentStep.totalSteps}
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Checklist as final timeline item */}
                  <div className="timeline-item timeline-final">
                    <div className="timeline-marker">
                      <RiCheckLine size={20} />
                    </div>
                    <div className="timeline-content">
                      <h5 className="mb-1">Abschluss-Checkliste</h5>
                      <p className="text-muted mb-0">
                        Überprüfe, was du in diesem Modul gelernt hast
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile and tablet portrait lesson info */}
              <div className={isTabletPortraitMode ? "mobile-lesson-info" : "d-block d-md-none mobile-lesson-info"}>
                <div className="current-lesson-card">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="lesson-status-icon">
                      <Icon name="clock" size="lg" />
                    </div>
                    <div>
                      <h5 className="mb-1 text-primary">Aktuelle Lektion</h5>
                      <p className="text-muted mb-0">Lektion {currentModule.currentLesson} von {currentModule.totalLessons}</p>
                    </div>
                  </div>
                  
                  <div className="current-lesson-details">
                    <h6 className="mb-2">{lessons.find(l => l.current)?.title}</h6>
                    <div className="current-step-mobile">
                      <p className="mb-2 text-muted">
                        <strong>{currentStep.title}</strong>
                      </p>
                      <div className="d-flex align-items-center gap-2">
                        <span className="step-type-badge">
                          {currentStep.type === 'video' ? <><Icon name="video" size="sm" /> Video</> : 
                           currentStep.type === 'task' ? <><Icon name="task" size="sm" /> Aufgabe</> :
                           currentStep.type === 'worksheet' ? <><Icon name="worksheet" size="sm" /> Arbeitsblatt</> :
                           <><Icon name="explanation" size="sm" /> Erklärung</>}
                        </span>
                        <small className="text-muted">
                          Schritt {currentStep.stepNumber} von {currentStep.totalSteps}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 mt-md-4 progress-actions">
                <Button 
                  variant="primary" 
                  size="lg"
                  className="w-100 w-md-auto"
                  onClick={() => setActiveGame('modules')}
                >
                  Weiter lernen →
                </Button>
              </div>
            </div>
          </Col>

          <Col xs={12} lg={4} className="order-1 order-lg-2">
            <Card className="quick-access-card mb-3 mb-md-4">
              <Card.Body>
                <h5 className="mb-3 mb-md-4">Schnellzugriff</h5>
                <div className="d-grid gap-2 gap-md-3">
                  <Button 
                    variant="outline-primary"
                    size="lg"
                    className="quick-access-btn"
                    onClick={() => setActiveGame('games')}
                  >
                    <div className="quick-access-icon"><Icon name="games" size="lg" color="#4A4A4A" /></div>
                    <div>
                      <strong>Spiele</strong>
                      <small className="d-block">Memory, Geschichten & mehr</small>
                    </div>
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="lg"
                    className="quick-access-btn"
                    onClick={() => {
                      setSelectedMaterialCategory(null);
                      setActiveGame('materials');
                    }}
                  >
                    <div className="quick-access-icon"><Icon name="materials" size="lg" color="#4A4A4A" /></div>
                    <div>
                      <strong>Lernmaterialien</strong>
                      <small className="d-block">Arbeitsblätter & Übungen</small>
                    </div>
                  </Button>
                  <Button 
                    variant="outline-primary"
                    size="lg"
                    className="quick-access-btn"
                    onClick={() => setActiveGame('modules')}
                  >
                    <div className="quick-access-icon"><Icon name="modules" size="lg" color="#4A4A4A" /></div>
                    <div>
                      <strong>Alle Module</strong>
                      <small className="d-block">Strukturiertes Lernen</small>
                    </div>
                  </Button>
                  <Button 
                    variant="outline-primary"
                    size="lg"
                    className="quick-access-btn"
                    onClick={() => window.open('https://cal.com/sprechhelden/sprechhelden-personliche-beratung-fur-eltern-mitglieder', '_blank')}
                  >
                    <div className="quick-access-icon"><Icon name="calendar" size="lg" color="#4A4A4A" /></div>
                    <div>
                      <strong>Termin buchen</strong>
                      <small className="d-none d-xl-block">Beratungsgespräch vereinbaren</small>
                    </div>
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {watchedVideos.length > 0 && (
              <Card className="last-used-card">
                <Card.Body>
                  <h5 className="mb-3 mb-md-4">Zuletzt angesehene Videos</h5>
                  <div className="d-grid gap-2 gap-md-3">
                    {watchedVideos.map(video => (
                      <div 
                        key={video.id} 
                        className="last-used-item" 
                        onClick={() => window.open(video.fileUrl, '_blank')}
                      >
                        <div className="material-icon-small">
                          <Icon name="video" size="lg" color="#4A4A4A" />
                        </div>
                        <div>
                          <strong>{video.title}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    );
  };

  const renderActiveGame = () => {
    // Restrict games-only users to only games-related views
    const gamesOnlyAccess = hasGamesOnlyAccess();
    const allowedViewsForGamesOnly = ['games', 'memory', 'story', 'wordmatch', 'suchbilder', 'minimalpair', 'settings', 'subscription', 'materials'];

    // If user has games-only access, redirect to games (no home for them)
    if (gamesOnlyAccess && activeGame === 'home') {
      setActiveGame('games');
      return <GamesOverview onGameSelect={handleNavigateToGame} />;
    }

    // If user has games-only access and tries to access restricted content, redirect to games
    if (gamesOnlyAccess && !allowedViewsForGamesOnly.includes(activeGame)) {
      setActiveGame('games');
      return <GamesOverview onGameSelect={handleNavigateToGame} />;
    }

    switch(activeGame) {
      case 'home':
        return renderHomeScreen();
      case 'modules':
        return selectedModuleId ?
          <ModuleView
            moduleId={selectedModuleId}
            moduleData={selectedModuleData}
            onBack={handleBackToModules}
            onModuleComplete={moduleCompleteFunction}
            onNavigateToGame={handleNavigateToGame}
            onNavigateToMaterials={handleNavigateToMaterials}
            onNavigateToNextModule={handleNavigateToNextModule}
          /> :
          <ModuleOverview onModuleSelect={handleModuleSelect} />;
      case 'games':
        return <GamesOverview onGameSelect={handleNavigateToGame} />;
      case 'memory':
        return <MemoryGame difficulty={difficulty || localDifficulty} onBackToGames={() => setActiveGame('games')} />;
      case 'story':
        return <StoryGame difficulty={difficulty || localDifficulty} onBackToGames={() => setActiveGame('games')} />;
      case 'wordmatch':
        return <WordMatchGame selectedSyllables={selectedSyllables} difficulty={difficulty || localDifficulty} onBackToGames={() => setActiveGame('games')} />;
      case 'suchbilder':
        return <SuchbilderGame difficulty={difficulty || localDifficulty} onBackToGames={() => setActiveGame('games')} />;
      case 'minimalpair':
        return <MinimalPairGame difficulty={difficulty || localDifficulty} onBackToGames={() => setActiveGame('games')} />;
      case 'materials':
        return <LearningMaterials initialCategory={selectedMaterialCategory} />;
      case 'theory':
        return <TheoreticalBackground />;
      case 'settings':
        return <Settings onNavigate={setActiveGame} onSettingsSaved={onSettingsSaved} />;
      case 'subscription':
        return <SubscriptionPage setHasSubscription={() => {}} onNavigate={setActiveGame} />;
      default:
        return renderHomeScreen();
    }
  };

  return (
    <div className={`content-area ${activeGame === 'suchbilder' ? 'suchbilder-game' : ''} ${activeGame === 'wordmatch' ? 'wordmatch-game' : ''} ${activeGame === 'subscription' ? 'subscription-page' : ''}`}>
      {renderActiveGame()}
      
      <WelcomeModal
        show={showWelcomeModal}
        userData={userData}
        onClose={async () => {
          setShowWelcomeModal(false);
          // Save that the user has seen the modal to the database
          try {
            const user = auth.currentUser;
            if (user) {
              await updateDoc(doc(db, 'users', user.uid), {
                hasSeenWelcomeModal: true
              });
            }
          } catch (error) {
            console.error('Error updating hasSeenWelcomeModal:', error);
          }
        }}
        onTestStart={() => {
          // Show the Aussprache test modal
          setShowAusspracheTest(true);
        }}
      />
      
      <AusspracheTestModal
        show={showAusspracheTest}
        onClose={() => {
          setShowAusspracheTest(false);
          setInProgressTestData(null);
        }}
        initialState={inProgressTestData}
      />
      
      <DifficultyModal
        show={showDifficultyModal}
        onHide={() => setShowDifficultyModal(false)}
        onSelect={handleDifficultySelect}
        gameName={selectedGameInfo.name}
      />
    </div>
  );
}

export default Dashboard;